/**
 * Phase 6 — llms.txt generator page.
 *
 * Generates a llms.txt body from the website's niche + top-performing
 * prompts, lets the user edit it freely, then offers a download
 * button that saves the file as `llms.txt` so they can publish it
 * at their site root (per the llmstxt.org spec).
 *
 * No state is persisted server-side — the user's edits live in the
 * browser until they hit Download.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { llmsTxt as llmsTxtApi } from "../../api/endpoints";
import { useSelectedWebsite } from "./Layout.jsx";

export default function LlmsTxt() {
  const { t } = useTranslation();
  const { websiteId } = useSelectedWebsite() || {};

  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");
  const [pristine, setPristine] = useState(true); // false = user edited
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [existing, setExisting] = useState(null);
  const [probing, setProbing] = useState(false);
  const textareaRef = useRef(null);

  const regenerate = useCallback(
    async (desc, { probe = false } = {}) => {
      if (!websiteId) return;
      if (probe) setProbing(true);
      else setLoading(true);
      setError(null);
      try {
        const r = await llmsTxtApi.generate(websiteId, {
          description: desc || undefined,
          probe,
        });
        setContent(r.data?.content || "");
        setPristine(true);
        if (probe && r.data?.existing !== undefined) {
          setExisting(r.data.existing);
        }
      } catch (err) {
        setError(
          err?.response?.data?.detail || t("llmsTxt.generateError"),
        );
      } finally {
        setLoading(false);
        setProbing(false);
      }
    },
    [websiteId, t],
  );

  // Initial load once a website is selected. We probe on the first
  // load so the user immediately sees "you already have an llms.txt"
  // when applicable — subsequent regenerates skip the probe.
  useEffect(() => {
    if (websiteId) regenerate("", { probe: true });
  }, [websiteId, regenerate]);

  function onContentChange(e) {
    setContent(e.target.value);
    setPristine(false);
  }

  function download() {
    // Browsers don't let JS write files directly — we mint an
    // anchor with a Blob URL, click it, and clean up. Standard
    // pattern, no extra dependencies.
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "llms.txt";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function copyToClipboard() {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select the textarea so the user can ctrl-C.
      textareaRef.current?.select();
    }
  }

  if (!websiteId) {
    return <div className="text-slate-500">{t("prompts.pickWebsite")}</div>;
  }

  function recheckExisting() {
    regenerate(description, { probe: true });
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">
          {t("llmsTxt.title")}
        </h1>
        <p className="text-sm text-slate-600 mt-1">{t("llmsTxt.subtitle")}</p>
      </header>

      <ExistingFileBanner
        existing={existing}
        probing={probing}
        onRecheck={recheckExisting}
        t={t}
      />

      <section className="card space-y-3">
        <label className="text-sm font-medium text-slate-900 block">
          {t("llmsTxt.descriptionLabel")}
        </label>
        <p className="text-xs text-slate-500">
          {t("llmsTxt.descriptionHint")}
        </p>
        <textarea
          className="input w-full min-h-[80px]"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t("llmsTxt.descriptionPlaceholder")}
          disabled={loading}
        />
        <div>
          <button
            type="button"
            className="btn-primary"
            onClick={() => regenerate(description)}
            disabled={loading}
          >
            {loading ? t("llmsTxt.generating") : t("llmsTxt.regenerate")}
          </button>
        </div>
      </section>

      {error && (
        <div className="card border-red-200 bg-red-50/80 text-sm text-red-800">
          {error}
        </div>
      )}

      <section className="card space-y-3">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">
              {t("llmsTxt.editorTitle")}
            </h2>
            <p className="text-xs text-slate-500">
              {pristine
                ? t("llmsTxt.editorPristine")
                : t("llmsTxt.editorEdited")}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={copyToClipboard}
              className="rounded-md px-3 py-2 text-sm ring-1 ring-slate-300 hover:bg-slate-50"
            >
              {copied ? t("llmsTxt.copied") : t("llmsTxt.copy")}
            </button>
            <button
              type="button"
              onClick={download}
              className="btn-primary"
              disabled={!content}
            >
              {t("llmsTxt.download")}
            </button>
          </div>
        </div>
        <textarea
          ref={textareaRef}
          className="input w-full font-mono text-sm min-h-[420px]"
          value={content}
          onChange={onContentChange}
          spellCheck={false}
          disabled={loading}
        />
      </section>

      <section className="card text-xs text-slate-600">
        <p className="font-medium text-slate-900 mb-1">
          {t("llmsTxt.publishTitle")}
        </p>
        <ol className="list-decimal pl-5 space-y-1">
          <li>{t("llmsTxt.publishStep1")}</li>
          <li>{t("llmsTxt.publishStep2")}</li>
          <li>{t("llmsTxt.publishStep3")}</li>
        </ol>
      </section>
    </div>
  );
}

/**
 * Status banner for the live llms.txt probe against the user's site.
 * Three states:
 *   - probing           → muted "checking…" line
 *   - existing.exists   → green "already published" banner with link
 *   - existing && !exists → amber "recommended to publish" banner
 *   - existing === null → no banner (probe hasn't run yet)
 */
function ExistingFileBanner({ existing, probing, onRecheck, t }) {
  if (probing) {
    return (
      <div className="card text-xs text-slate-500">
        {t("llmsTxt.probing")}
      </div>
    );
  }
  if (!existing) return null;

  if (existing.exists) {
    return (
      <div className="card border-emerald-200 bg-emerald-50/80 text-sm text-emerald-900 space-y-1">
        <div className="font-medium">{t("llmsTxt.existingFoundTitle")}</div>
        <p>
          {t("llmsTxt.existingFoundBody")}{" "}
          <a
            href={existing.url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            {existing.url}
          </a>
        </p>
        <button
          type="button"
          onClick={onRecheck}
          className="text-xs underline text-emerald-800 hover:text-emerald-900"
        >
          {t("llmsTxt.recheck")}
        </button>
      </div>
    );
  }

  return (
    <div className="card border-amber-200 bg-amber-50/80 text-sm text-amber-900 space-y-1">
      <div className="font-medium">{t("llmsTxt.existingMissingTitle")}</div>
      <p>{t("llmsTxt.existingMissingBody")}</p>
      {existing.error && (
        <p className="text-xs text-amber-800/90">
          {t("llmsTxt.probeNote", { error: existing.error })}
        </p>
      )}
      <button
        type="button"
        onClick={onRecheck}
        className="text-xs underline text-amber-800 hover:text-amber-900"
      >
        {t("llmsTxt.recheck")}
      </button>
    </div>
  );
}
